// src/components/AffiliateSlot.jsx
//
// Reusable affiliate placement slot. Used across state pages, city pages,
// service pages, cost pages, etc. Two responsibilities:
//
//   1. Reserve consistent visual real estate in every page template so the
//      affiliate banner can be flipped on site-wide by changing one prop
//      default (AFFILIATE_LIVE) without re-touching every page.
//
//   2. While the slot is in placeholder mode (AFFILIATE_LIVE = false), it
//      renders as a clean "Find a local cleaner" call-to-action linking
//      to /find-a-cleaner (the in-house waiting-list / funnel-prep page).
//      When live, it will render the partner banner (Angi / Thumbtack /
//      Networx / direct) with location-prefilled deep links.
//
// API:
//
//   <AffiliateSlot
//     variant="state" | "city" | "inline" | "compact"
//     location="Colorado" | "Denver, CO" | ...
//     theme={T}        // pass the theme object from the calling page
//     fontDisplay      // optional font overrides; defaults match site
//     fontBody
//     fontMono
//   />
//
// `variant` controls the visual treatment:
//   - "state":   full-width hero card, sienna accent border, ~120px tall
//   - "city":    same hero treatment, shorter copy
//   - "inline":  smaller mid-page card for inside per-city blocks
//   - "compact": single-line strip for sidebar / TOC areas
//
// When a partner deal is signed, set AFFILIATE_LIVE = true and update
// PARTNER_CONFIG with the partner's tracking URL pattern and brand
// styling. No page-level edits required.

import React from 'react';
import { Link } from 'react-router-dom';

// Feature flag — flip to true once a partner is signed.
const AFFILIATE_LIVE = false;

// Future partner config (unused while AFFILIATE_LIVE is false). The shape
// is documented now so the flip-the-switch session is one file edit.
//
// const PARTNER_CONFIG = {
//   name: 'Networx',
//   ctaLabel: 'Get 3 free quotes',
//   urlPattern: (location) =>
//     `https://www.networx.com/partner/windowwashingguide?service=window-cleaning&loc=${encodeURIComponent(location)}`,
//   disclosureText: 'We may earn a commission if you book through this link.',
// };

export default function AffiliateSlot({
  variant = 'state',
  location = '',
  theme,
  fontDisplay = "'Fraunces', serif",
  fontBody = "'Inter Tight', sans-serif",
  fontMono = "'JetBrains Mono', monospace",
}) {
  if (!theme) return null;
  const T = theme;

  // Destination — placeholder routes to /find-a-cleaner with a location query
  // string so we can capture intent from day one. When AFFILIATE_LIVE flips
  // on, the partner URL replaces this href.
  const href = AFFILIATE_LIVE
    ? '#' // PARTNER_CONFIG.urlPattern(location)
    : `/find-a-cleaner${location ? `?location=${encodeURIComponent(location)}` : ''}`;

  // Copy varies slightly by variant + whether a location is supplied.
  const eyebrow = AFFILIATE_LIVE ? 'SPONSORED' : 'LOCAL PROS';
  const headline = location
    ? `Need a window cleaner in ${location}?`
    : 'Need a window cleaner near you?';
  const subline = AFFILIATE_LIVE
    ? 'Get 3 free quotes from local pros — typically respond within an hour.'
    : "We're building a local pro directory. Join the free waitlist.";
  const cta = AFFILIATE_LIVE ? 'Get free quotes →' : 'Join the waitlist →';

  // ─────────────────────────────────────────────────────────────────────
  // COMPACT VARIANT — single-line strip, used in TOC sidebars
  // ─────────────────────────────────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <Link
        to={href}
        style={{
          display: 'block',
          padding: '12px 14px',
          background: T.surface,
          border: `1px solid ${T.rule}`,
          borderLeft: `2px solid ${T.sienna}`,
          textDecoration: 'none',
          marginTop: 16,
        }}
      >
        <div style={{
          fontFamily: fontMono, fontSize: 9,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 4,
        }}>
          {eyebrow}
        </div>
        <div style={{
          fontFamily: fontDisplay, fontSize: 13,
          color: T.ink, lineHeight: 1.35,
        }}>
          {location ? `Local pros in ${location} →` : 'Join the waitlist →'}
        </div>
      </Link>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // INLINE VARIANT — used inside per-city blocks on state pages
  // ─────────────────────────────────────────────────────────────────────
  if (variant === 'inline') {
    return (
      <Link
        to={href}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          padding: '12px 16px',
          background: T.surface,
          border: `1px solid ${T.rule}`,
          borderLeft: `2px solid ${T.sienna}`,
          textDecoration: 'none',
          marginTop: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{
            fontFamily: fontMono, fontSize: 9,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 4,
          }}>
            {eyebrow}
          </div>
          <div style={{
            fontFamily: fontDisplay, fontSize: 14,
            color: T.ink, lineHeight: 1.35,
          }}>
            {location ? `Window cleaners in ${location}` : 'Window cleaners near you'}
          </div>
        </div>
        <div style={{
          fontFamily: fontMono, fontSize: 10,
          letterSpacing: '0.18em', color: T.sienna,
          whiteSpace: 'nowrap',
        }}>
          {cta.toUpperCase()}
        </div>
      </Link>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // STATE / CITY VARIANT — full hero card
  // ─────────────────────────────────────────────────────────────────────
  return (
    <Link
      to={href}
      style={{
        display: 'block',
        padding: '22px 24px',
        background: T.surface,
        border: `1px solid ${T.rule}`,
        borderLeft: `3px solid ${T.sienna}`,
        textDecoration: 'none',
        marginBottom: 40,
      }}
    >
      <div style={{
        fontFamily: fontMono, fontSize: 11,
        letterSpacing: '0.18em', color: T.sienna,
        marginBottom: 10,
      }}>
        {eyebrow}
      </div>
      <h3 style={{
        fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
        lineHeight: 1.2, letterSpacing: '-0.01em',
        margin: '0 0 8px', color: T.ink,
      }}>
        {headline}
      </h3>
      <p style={{
        fontFamily: fontBody, fontSize: 14,
        lineHeight: 1.5, color: T.inkSoft,
        margin: '0 0 14px',
      }}>
        {subline}
      </p>
      <div style={{
        fontFamily: fontMono, fontSize: 11,
        letterSpacing: '0.18em', color: T.sienna,
      }}>
        {cta.toUpperCase()}
      </div>
    </Link>
  );
}
